import { useEffect, useMemo, useState } from "react";
import { mutate, useApi } from "../api/client";
import { usePermissions } from "../api/permissions";
import type { RolesResponse } from "../api/types";
import { Empty, ErrorState, Loading } from "../components/States";
import { Breadcrumb } from "../components/Toolbar";
import { fmtDateTime } from "../lib/format";

const ROLES_PATH = "/api/orchestrator/roles";

/**
 * What each role may do, page by page.
 *
 * The permission catalogue comes from the server grouped by page, so this
 * screen has no list of its own to keep in step. A permission added to
 * `src/permissions.py` appears here with its description and nothing in this
 * file changes.
 *
 * Laid out by page rather than as a flat list of permission strings, because
 * "what can a reviewer see" is a question about pages. Within a page, `view`
 * sorts first: it is the one that gates the rest, and switching it off while
 * leaving `export` on would be incoherent — which is why doing that disables
 * the others rather than silently allowing it.
 */
export function Roles() {
  const { data, error, loading, reload } = useApi<RolesResponse>(ROLES_PATH);
  const { can } = usePermissions();
  const mayManage = can("users:manage");

  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const role = useMemo(
    () => data?.roles.find((r) => r.name === selected) ?? data?.roles[0] ?? null,
    [data, selected],
  );

  // Re-seed whenever the server's answer or the selection changes. Not merged
  // with the previous draft: switching role mid-edit and keeping the old
  // toggles would write one role's intent onto another.
  useEffect(() => {
    if (role) setDraft(new Set(role.permissions));
    setSaveError(null);
    setSaved(null);
  }, [role]);

  if (loading) return <div className="card"><Loading what="roles" /></div>;
  if (error)
    return <div className="card"><ErrorState message={error} onRetry={reload} /></div>;
  if (!data || !role || !draft) return null;

  const stored = new Set(role.permissions);
  const added = [...draft].filter((p) => !stored.has(p));
  const removed = [...stored].filter((p) => !draft.has(p));
  const dirty = added.length + removed.length;

  const toggle = (permission: string) => {
    setSaved(null);
    setSaveError(null);
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) {
        next.delete(permission);
        // Turning off `view` turns off everything else on that page. Keeping
        // `documents:export` while removing `documents:view` would grant a
        // bulk export of a table the holder cannot open, which is not a state
        // anyone means to create.
        const section = permission.split(":")[0];
        if (permission.endsWith(":view")) {
          for (const p of [...next]) {
            if (p.startsWith(`${section}:`)) next.delete(p);
          }
        }
      } else {
        next.add(permission);
        // And granting anything on a page grants the right to open it.
        const section = permission.split(":")[0];
        const view = `${section}:view`;
        if (view in flatCatalogue(data)) next.add(view);
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await mutate<{ added: string[]; removed: string[] }>(
        `${ROLES_PATH}/${encodeURIComponent(role.name)}`,
        "PUT",
        { permissions: [...draft] },
      );
      setSaved(
        `Saved. ${res.added.length} granted, ${res.removed.length} removed — ` +
          `takes effect on each holder's next request.`,
      );
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Breadcrumb trail={[{ label: "Users", to: "/users" }, { label: "Roles" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Roles and permissions</h1>
          <p className="page-sub">
            What each role may see and do. A person is given a role on the Users
            screen; the role decides which pages appear for them.
          </p>
        </div>
      </div>

      {!mayManage ? (
        <div className="notice">
          You can see how roles are configured but not change them. Editing needs
          the <code>users:manage</code> permission.
        </div>
      ) : null}

      <div className="role-layout">
        <div className="card role-list">
          <div className="card-head bordered">
            <span className="card-title">Roles</span>
          </div>
          <div className="card-body" style={{ padding: 8 }}>
            {data.roles.map((r) => (
              <button
                key={r.name}
                className={`role-pick${r.name === role.name ? " on" : ""}`}
                onClick={() => setSelected(r.name)}
              >
                <span className="role-pick-name">{r.label}</span>
                <span className="role-pick-meta">
                  {r.permissions.length} permission
                  {r.permissions.length === 1 ? "" : "s"}
                  {r.active_users ? ` · ${r.active_users} user${r.active_users === 1 ? "" : "s"}` : ""}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="card role-detail">
          <div className="card-head bordered">
            <span className="card-title">{role.label}</span>
            <span className="muted">
              {role.is_system ? "built in" : "custom"}
              {role.updated_by ? ` · last changed by ${role.updated_by}` : ""}
              {role.updated_at ? `, ${fmtDateTime(role.updated_at)}` : ""}
            </span>
          </div>
          <div className="card-body">
            <p className="page-sub" style={{ marginTop: 0 }}>{role.description}</p>

            {role.active_users === 0 ? (
              <div className="notice">Nobody currently holds this role.</div>
            ) : null}
            {saved ? <div className="notice">{saved}</div> : null}
            {saveError ? <div className="notice notice-error">{saveError}</div> : null}

            {Object.entries(data.catalogue).length === 0 ? (
              <Empty title="No permissions defined" />
            ) : (
              Object.entries(data.catalogue).map(([section, perms]) => {
                const viewPerm = perms.find((p) => p.action === "view");
                const pageOn = !viewPerm || draft.has(viewPerm.permission);
                return (
                  <div key={section} className="perm-section">
                    <div className="perm-section-head">{section}</div>
                    {perms.map((p) => {
                      const on = draft.has(p.permission);
                      const changed = on !== stored.has(p.permission);
                      // Everything but `view` needs the page to be visible.
                      const blocked = p.action !== "view" && !pageOn;
                      return (
                        <label
                          key={p.permission}
                          className={`perm-row${changed ? " changed" : ""}${blocked ? " blocked" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={!mayManage || saving || blocked}
                            onChange={() => toggle(p.permission)}
                          />
                          <span className="perm-what">
                            {p.description}
                            {blocked ? (
                              <span className="muted"> — needs {section} view</span>
                            ) : null}
                          </span>
                          <span className={`perm-action a-${p.action}`}>{p.action}</span>
                          <span className="mono perm-name">{p.permission}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })
            )}

            {mayManage ? (
              <div className="form-actions">
                <span className="muted actions-note">
                  {dirty === 0
                    ? "No changes."
                    : `${added.length} granted, ${removed.length} removed. ` +
                      `Takes effect on each holder's next request.`}
                </span>
                <button
                  className="btn"
                  onClick={() => setDraft(new Set(role.permissions))}
                  disabled={dirty === 0 || saving}
                >
                  Reset
                </button>
                <button
                  className="btn btn-primary"
                  onClick={save}
                  disabled={dirty === 0 || saving}
                >
                  {saving ? "Saving…" : `Save ${dirty || ""}`.trim()}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

/** Every permission in the catalogue, keyed by name. */
function flatCatalogue(data: RolesResponse): Record<string, true> {
  const out: Record<string, true> = {};
  for (const perms of Object.values(data.catalogue)) {
    for (const p of perms) out[p.permission] = true;
  }
  return out;
}
